import { useContext, useMemo } from 'react'
import { OrderType } from 'components/PromotionList'
import { AdjustmentTypeEnumType } from 'enums'
import { getImageResizeableURL } from 'helpers/image'
import {
  AdjustmentSourceEnumType,
  type OrderAndFpMemberCouponsQuery,
  type OrderAndHhMemberCouponsQuery,
} from 'graphQL/dinein/generate/operations'
import { CouponType } from 'components/PromotionDetails'

import { THEMES_UI } from 'brands'
import { AppContext } from 'contexts/AppProvider'

type ManualPromotion =
  | OrderAndFpMemberCouponsQuery['orderAndFPMemberCoupons']['manualPromotions'][number]
  | OrderAndHhMemberCouponsQuery['orderAndHHMemberCoupons']['manualPromotions'][number]

interface UsePromotionProps {
  orderAndFpMemberCouponsResult: OrderAndFpMemberCouponsQuery | undefined
  orderAndHhMemberCouponsResult: OrderAndHhMemberCouponsQuery | undefined
}

function usePromotion({ orderAndFpMemberCouponsResult, orderAndHhMemberCouponsResult }: UsePromotionProps) {
  const { themeUI, isThaiLang } = useContext(AppContext)
  const { orderAndMemberCoupons, memberCouponsFP, memberPromotions, generalPromotions, memberCouponsHH } =
    useMemo(() => {
      let orderAndMemberCoupons: OrderType | undefined = undefined
      let memberCouponsFP: CouponType[] = []
      let memberCouponsHH: CouponType[] = []
      const memberPromotions: ManualPromotion[] = []
      const generalPromotions: ManualPromotion[] = []

      if (orderAndFpMemberCouponsResult && themeUI === THEMES_UI.GON) {
        const {
          orderAndFPMemberCoupons: { order, coupons, manualPromotions },
        } = orderAndFpMemberCouponsResult
        orderAndMemberCoupons = order
        memberCouponsFP = coupons

        manualPromotions.forEach((promotion) => {
          const memberTierRankConditionsLength = promotion.memberTierRankConditions.length
          if (memberTierRankConditionsLength > 0) {
            memberPromotions.push(promotion)
          } else if (memberTierRankConditionsLength === 0) {
            generalPromotions.push(promotion)
          }
        })
      } else if (orderAndHhMemberCouponsResult && themeUI !== THEMES_UI.GON) {
        const {
          orderAndHHMemberCoupons: { order, coupons, manualPromotions },
        } = orderAndHhMemberCouponsResult
        orderAndMemberCoupons = order
        coupons.forEach(({ coupon, isAvailable }) => {
          let thumbnailImageURL = ''
          if (coupon.promotion.images.length > 0) {
            thumbnailImageURL = getImageResizeableURL(coupon.promotion.images, {
              width: '140',
              height: '140',
            })
          }

          memberCouponsHH.push({
            id: coupon.id,
            isAvailable: isAvailable,
            endTime: coupon.endDate,
            serialNumber: '',
            rewardType: AdjustmentTypeEnumType.Discount,
            thumbnailImageURL,
            name: isThaiLang ? coupon.promotion.nameTh : coupon.promotion.nameEn,
            description: isThaiLang ? coupon.promotion.descriptionTh : coupon.promotion.descriptionEn,
            source: AdjustmentSourceEnumType.MemberCoupon,
            memberCouponRefID: '',
          })
        })

        manualPromotions.forEach((promotion) => {
          const memberTierRankConditionsLength = promotion.memberTierRankConditions.length
          if (memberTierRankConditionsLength > 0) {
            memberPromotions.push(promotion)
          } else if (memberTierRankConditionsLength === 0) {
            generalPromotions.push(promotion)
          }
        })
      }

      return {
        orderAndMemberCoupons,
        memberCouponsFP,
        memberPromotions,
        generalPromotions,
        memberCouponsHH,
      }
    }, [orderAndFpMemberCouponsResult, orderAndHhMemberCouponsResult, isThaiLang, themeUI])

  return {
    orderAndMemberCoupons,
    memberCouponsFP,
    memberPromotions,
    generalPromotions,
    memberCouponsHH,
  }
}

export default usePromotion
